import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { boxName, styleName } from "@/lib/catalog";

export default function Admin() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        if (!loading && (!user || user.role !== "admin")) navigate("/login");
    }, [loading, user, navigate]);

    useEffect(() => {
        if (user?.role === "admin") {
            api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
            api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
        }
    }, [user]);

    if (!user || user.role !== "admin") return <div className="pt-44" />;

    const cards = [
        { label: "Revenue", value: stats ? `€${stats.revenue}` : "—" },
        { label: "Paid Orders", value: stats?.paid_orders ?? "—" },
        { label: "Total Orders", value: stats?.total_orders ?? "—" },
        { label: "Customers", value: stats?.customers ?? "—" },
    ];

    return (
        <div data-testid="admin-page" className="pt-28 md:pt-36 min-h-[70vh]">
            <div className="max-w-[1300px] mx-auto px-5 sm:px-8">
                <span className="eyebrow text-ink/50">Admin</span>
                <h1 className="mt-4 font-serif font-light text-5xl tracking-[-0.04em]">
                    Dashboard
                </h1>

                <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-line border border-line">
                    {cards.map((c) => (
                        <div key={c.label} className="bg-paper p-8" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g,"-")}`}>
                            <p className="eyebrow text-ink/45">{c.label}</p>
                            <p className="mt-4 font-serif text-4xl tracking-[-0.03em]">
                                {c.value}
                            </p>
                        </div>
                    ))}
                </div>

                <h2 className="mt-16 eyebrow text-ink/50">Orders</h2>
                <div className="mt-6 border border-line overflow-x-auto">
                    <table className="w-full text-sm" data-testid="orders-table">
                        <thead>
                            <tr className="border-b border-line text-left text-ink/50 eyebrow">
                                <th className="p-4 font-medium">Date</th>
                                <th className="p-4 font-medium">Customer</th>
                                <th className="p-4 font-medium">Items</th>
                                <th className="p-4 font-medium">Total</th>
                                <th className="p-4 font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => (
                                <tr key={o.id} className="border-b border-line last:border-0">
                                    <td className="p-4 text-ink/60 whitespace-nowrap">
                                        {new Date(o.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="p-4 text-ink/70">{o.email || "Guest"}</td>
                                    <td className="p-4 text-ink/70 max-w-xs">
                                        {o.items.map((it, i) => (
                                            <div key={i} className="truncate">
                                                {it.quantity}× {boxName(it.category, it.boxType)} ·{" "}
                                                {it.boxType === "starter" ? "Assorted" : styleName(it.category, it.style)} · {it.color}
                                            </div>
                                        ))}
                                    </td>
                                    <td className="p-4 font-serif text-base">€{o.amount}</td>
                                    <td className="p-4">
                                        <span
                                            className={`eyebrow ${
                                                o.payment_status === "paid"
                                                    ? "text-ink"
                                                    : "text-ink/40"
                                            }`}
                                        >
                                            {o.payment_status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {orders.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="p-10 text-center text-ink/50">
                                        No orders yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
